import axios from 'axios';

export const API_BASE = 'https://spapi.webaccess.nl';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Global callback so AuthContext can register its refreshUser fn with the interceptor
let _onPermissionChanged: (() => Promise<void>) | null = null;
export const setPermissionRefreshCallback = (fn: () => Promise<void>) => {
  _onPermissionChanged = fn;
};

// Attach JWT token + active org to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const orgId = localStorage.getItem('active_org_id');
  if (orgId) {
    config.headers['X-Organization-ID'] = orgId;
  }
  return config;
});

// Auto-refresh token on 401; re-fetch permissions on 403
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const res = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          });
          localStorage.setItem('access_token', res.data.access_token);
          localStorage.setItem('refresh_token', res.data.refresh_token);
          originalRequest.headers.Authorization = `Bearer ${res.data.access_token}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('active_org_id');
          window.location.reload();
        }
      }
    }

    // On 403 (permission denied — role may have changed), refresh user permissions
    if (error.response?.status === 403 && !originalRequest._permRetry) {
      originalRequest._permRetry = true;
      if (_onPermissionChanged) {
        await _onPermissionChanged();
        // Retry the original request once with refreshed permissions
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// ── CSV exports ────────────────────────────────────────────────────────────────
// Streams the full dataset (all of the user's rows) from the backend and saves
// it as a file. Uses the authenticated axios instance so the bearer token / org
// header are attached automatically.
export const downloadExport = async (path: string, filename: string) => {
  const res = await api.get(path, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authRegister = (data: { email: string; full_name: string; password: string; invite_token?: string }) =>
  api.post('/api/v1/auth/register', data);

export const authLogin = (data: { email: string; password: string }) =>
  api.post('/api/v1/auth/login', data);

export const authRefresh = (refresh_token: string) =>
  api.post('/api/v1/auth/refresh', { refresh_token });

export const authGetMe = () =>
  api.get('/api/v1/auth/me');

export const authUpdateMe = (data: { full_name?: string; email?: string }) =>
  api.put('/api/v1/auth/me', data);

export const authChangePassword = (data: { old_password: string; new_password: string }) =>
  api.post('/api/v1/auth/change-password', data);

export const authForgotPassword = (email: string) =>
  api.post('/api/v1/auth/forgot-password', { email });

export const authResetPassword = (data: { token: string; new_password: string }) =>
  api.post('/api/v1/auth/reset-password', data);

// ── Pricing Formulas ──────────────────────────────────────────────────────────
export const getPricingFormulas = () =>
  api.get('/api/v1/settings/pricing-formulas');

export const upsertPricingFormula = (marketplace_code: string, formula: string) =>
  api.put(`/api/v1/settings/pricing-formulas/${marketplace_code}`, { formula });

export const deletePricingFormula = (marketplace_code: string) =>
  api.delete(`/api/v1/settings/pricing-formulas/${marketplace_code}`);

export const validatePricingFormula = (formula: string, sample_base_price: number, sample_cost_price?: number) =>
  api.post('/api/v1/settings/pricing-formulas/validate', { formula, sample_base_price, sample_cost_price });

// ── Organizations ─────────────────────────────────────────────────────────────
export const getMyOrganizations = () =>
  api.get('/api/v1/organizations/me');

export const createOrganization = (name: string) =>
  api.post('/api/v1/organizations', { name });

export const getOrganization = (orgId: string) =>
  api.get(`/api/v1/organizations/${orgId}`);

export const updateOrganization = (orgId: string, name: string) =>
  api.put(`/api/v1/organizations/${orgId}`, { name });

// ── User Management (admin-only) ──────────────────────────────────────────────
export const getUsers = () =>
  api.get('/api/v1/users');

export const createUser = (data: { email: string; full_name: string; password: string; role_id?: string; is_admin?: boolean }) =>
  api.post('/api/v1/users', data);

export const updateUser = (userId: string, data: { full_name?: string; email?: string; new_password?: string; role_id?: string | null; is_active?: boolean; is_admin?: boolean }) =>
  api.put(`/api/v1/users/${userId}`, data);

export const deactivateUser = (userId: string) =>
  api.delete(`/api/v1/users/${userId}`);

export const getInvite = (token: string) =>
  api.get(`/api/v1/invites/${token}`);

export const acceptInvite = (token: string) =>
  api.post(`/api/v1/invites/${token}/accept`);

// ── Roles (admin-only, global) ────────────────────────────────────────────────
export const getRoles = () =>
  api.get('/api/v1/roles');

export const createRole = (data: { name: string; description?: string; permissions: string[] }) =>
  api.post('/api/v1/roles', data);

export const updateRole = (roleId: string, data: { name?: string; description?: string; permissions?: string[] }) =>
  api.put(`/api/v1/roles/${roleId}`, data);

export const deleteRole = (roleId: string) =>
  api.delete(`/api/v1/roles/${roleId}`);

export const getPermissions = () =>
  api.get('/api/v1/permissions');

// ── User Preferences ─────────────────────────────────────────────────────────
export const getUserPreferences = () =>
  api.get('/api/v1/user/preferences');

export const updateUserPreferences = (preferences: Record<string, any>) =>
  api.put('/api/v1/user/preferences', { preferences });

// ── SP-API Scheduler ──────────────────────────────────────────────────────────
export const getSchedulerConfig = (sellerId: string) =>
  api.get(`/api/v1/sp-api/accounts/${sellerId}/scheduler/config`);

export const updateSchedulerConfig = (sellerId: string, taskKey: string, isEnabled: boolean) =>
  api.put(`/api/v1/sp-api/accounts/${sellerId}/scheduler/config/${taskKey}`, { is_enabled: isEnabled });

// ── External API Keys ─────────────────────────────────────────────────────────
export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  is_revealable: boolean; // full key retrievable via reveal endpoint
}

export interface ApiKeyCreated extends ApiKey {
  api_key: string; // full secret, returned at creation
}

export const listApiKeys = () =>
  api.get<ApiKey[]>('/api/v1/sp-api/api-keys');

export const createApiKey = (data: { name: string; scopes?: string[]; expires_in_days?: number | null }) =>
  api.post<ApiKeyCreated>('/api/v1/sp-api/api-keys', data);

export const revealApiKey = (keyId: string) =>
  api.get<{ id: string; api_key: string }>(`/api/v1/sp-api/api-keys/${keyId}/reveal`);

export const revokeApiKey = (keyId: string) =>
  api.delete(`/api/v1/sp-api/api-keys/${keyId}`);

export default api;
